import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, JSX } from 'react';
import type { ModeId, ThemeFamilyId } from '@evi/skins';
import { THEMES } from '@evi/skins';
import { DefaultAnimation } from '../themes/default/DefaultAnimation.js';
import { resolveEnergySim } from '../components/charging/chargingTheme.js';
import { ShaderChargingAnimation } from '../components/charging/ShaderChargingAnimation.js';
import { SimChargingAnimation } from '../components/charging/SimChargingAnimation.js';
import type { FluidVariantId } from '../components/charging/sim/fluidSim.js';
import styles from './ChargingAnimation.module.css';

export interface ChargingAnimationProps {
  /**
   * Theme code — admin family ID ('default' | 'luce' | 'cartographic'
   * | 'neon' | 'electric-light' | 'dark-electric' | 'obsidian') or skin
   * code ('skin_midnight', 'skin_sunset', 'skin_neon', 'skin_obsidian',
   * 'skin_bloom').
   *
   * Loose string on purpose. Unknown codes silently fall through to
   * DefaultAnimation so forward-compat works when a new theme is
   * added registry-side before the dispatcher table catches up.
   */
  themeCode: string;
  /** Which light/dark variant the animation paints. */
  mode: ModeId;
  /** Battery state of charge, 0–100. */
  percent: number;
  /** Instantaneous power draw in kW — shown by themes that render a
   *  numeric readout. Driver Themes ticket: also drives uIntensity
   *  on the shader-rendered variants (powerKw / 50 → [0.2, 1.0]). */
  powerKw?: number;
  /**
   * Driver Themes ticket — cumulative kWh delivered this session.
   * Drives uKwhScale on the shader-rendered variants (kwhDelivered /
   * 60 → [0, 1]). Existing CSS-based animation modules ignore this
   * prop; it lands on the shared interface so the dispatcher can
   * spread it uniformly to whichever inner component the branch
   * picks.
   */
  kwhDelivered?: number;
  /**
   * True when the animation is the focal element of the page (the
   * driver is actively charging, or the picker's live preview is the
   * active theme). Drives halo + the wrapper's glow intensity.
   * Defaults to false — decorative renders (Themes page cards,
   * previews on the admin Unlocks page) should pass `active={false}`
   * so the breathing glow doesn't steal attention from adjacent
   * surfaces.
   */
  active?: boolean;
  /** Max-width / max-height in px. Canonical full-screen render is
   *  220. MiniChargingBar renders at 44. Preview cards pass ~140. */
  size?: number;
  /**
   * Wrapper glow treatment. Defaults track `active`:
   *   active=true  → 'breathing' (pulse between subtle + full)
   *   active=false → 'subtle' (static low-intensity rim)
   * Consumers that own their own chrome (MiniChargingBar — the bar
   * already has a bloom) pass `glow="none"` to suppress.
   */
  glow?: 'breathing' | 'subtle' | 'none';
  /**
   * Paint the accent colour locally on the wrapper — lets preview
   * surfaces render a skin that isn't currently applied in its own
   * colour without touching the global ThemeProvider overlay. The
   * CSS-component animations consume the CSS variable via their
   * stylesheets; the shader-rendered variants ignore the override
   * and use their per-variant ORB_UNIFORMS or hardcoded shader
   * constants instead.
   */
  accentOverride?: string;
  /** Paint the charging colour locally on the wrapper. Same scoping
   *  as accentOverride: CSS animations consume, shader variants
   *  ignore. */
  chargingOverride?: string;
}

/**
 * Skin codes that route to the shader path pre-migration. Driver
 * Themes ticket promotes `neon` and `obsidian` from skin overlays
 * to family-level themes; the migration script (Commit 10 of the
 * ticket) will rewrite users' `active_skin_code` values to the
 * matching family code. Until users have migrated, this alias map
 * routes the legacy skin codes through the same shader programs
 * the new family codes use.
 *
 * Per Surprise E: the `skin_neon` and `skin_obsidian` Unlock
 * documents stay in MongoDB for one release cycle, so both code
 * paths must surface valid animations during the transition.
 */
const SKIN_ALIAS_TO_SHADER: Record<string, { kind: 'neon' | 'obsidian' }> = {
  skin_neon: { kind: 'neon' },
  skin_obsidian: { kind: 'obsidian' },
};

/**
 * Theme-aware charging animation dispatcher. Picks the per-theme
 * module matching `themeCode` and renders it inside a glow frame.
 *
 * Dispatch order:
 *   1. Cartographic and the Midnight/Sunset/Bloom skins → interactive simulations.
 *   2. Family code in THEMES with chargingAnimation === 'shader' →
 *      ShaderChargingAnimation with the registry-declared
 *      shaderKind + shaderVariant.
 *   3. Skin code in SKIN_ALIAS_TO_SHADER → ShaderChargingAnimation
 *      with the aliased shaderKind (pre-migration compat).
 *   4. Anything else → DefaultAnimation for unknown codes.
 *
 * The frame wrapper, glow handling, and CSS-variable overrides
 * stay outside the branch — they apply to both shader and CSS
 * paths uniformly so the visual chrome (frame glow ring +
 * breathing pulse + size + override CSS vars) reads the same
 * regardless of which inner component renders.
 *
 * Canonical bounding box: 220px square (full-screen ChargingPage).
 * Mini size: 44px (MiniChargingBar chip). Every per-theme module
 * must render cleanly at both.
 */
export function ChargingAnimation(props: ChargingAnimationProps): JSX.Element {
  const { themeCode, active = false, size = 220, glow, accentOverride, chargingOverride } = props;

  // Only visible previews own a GL context. Otherwise the four new canvases
  // push a phone's context budget over the limit and can evict its map.
  const frameRef=useRef<HTMLDivElement>(null);
  const [visible,setVisible]=useState(typeof IntersectionObserver==='undefined');
  useEffect(()=>{
    if(typeof IntersectionObserver==='undefined'||!frameRef.current) return;
    const observer=new IntersectionObserver(([entry])=>setVisible(entry?.isIntersecting??false),{rootMargin:'80px'});
    observer.observe(frameRef.current);
    return ()=>observer.disconnect();
  },[]);
  const energySim=resolveEnergySim(themeCode,props.mode);

  const glowMode = glow ?? (active ? 'breathing' : 'subtle');
  const frameClass =
    glowMode === 'breathing'
      ? `${styles.frame} ${styles.frameBreathing}`
      : glowMode === 'subtle'
        ? `${styles.frame} ${styles.frameSubtle}`
        : styles.frame;

  // Inline vars serve two jobs:
  //   1. Pin the frame's dimensions so the glow rim tracks the inner
  //      animation instead of inheriting parent padding.
  //   2. Forward any colour override to the inner module — the
  //      per-theme animations consume --evi-accent / --evi-charging
  //      the same way DefaultChargingRing already does, so overrides
  //      scoped to this wrapper paint correctly without touching the
  //      global ThemeProvider. Shader variants ignore these overrides
  //      (they read uniform colours from ORB_UNIFORMS instead) but
  //      accepting the props at this layer keeps the dispatcher
  //      contract uniform across both paths.
  const style: CSSProperties = {
    width: size,
    height: size,
  };
  if (accentOverride) {
    (style as Record<string, string | number>)['--evi-accent'] = accentOverride;
  }
  if (chargingOverride) {
    (style as Record<string, string | number>)['--evi-charging'] = chargingOverride;
  }

  // Pick the inner content via the dispatch order documented
  // in the function header. The size resolved above (props.size ??
  // 220) propagates to whichever inner component renders so the
  // canvas / CSS element matches the frame's dimensions.
  const resolvedProps = { ...props, size };
  let inner: JSX.Element;

  // Branch 1: family code with a shader-flagged registry entry.
  const themeEntry = themeCode in THEMES
    ? THEMES[themeCode as ThemeFamilyId]
    : undefined;
  if (energySim) {
    inner=<SimChargingAnimation {...resolvedProps} simKind={energySim.kind} simVariant={energySim.variant} />;
  } else if (themeEntry?.chargingAnimation === 'shader' && themeEntry.shaderKind) {
    // Per-mode shader variant resolution. Aurora and Cyber are
    // the shader kinds whose variants are mode-dependent (aurora-
    // dark: emerald/magenta/teal on near-black, aurora-light:
    // pastel mint/pink/sky on warm cream; cyber-dark: hot
    // magenta/electric purple on dark navy, cyber-light: pastel
    // magenta/lavender on lavender cream). Orb / obsidian / neon
    // are mode-independent — their visual composition is the same
    // in light or dark mode; the surrounding chrome tokens carry
    // the mode flip. For aurora + cyber, the registry's
    // shaderVariant value is treated as a docstring default;
    // runtime mode wins.
    let resolvedShaderVariant = themeEntry.shaderVariant;
    if (themeEntry.shaderKind === 'aurora') {
      resolvedShaderVariant = (props.mode ? `aurora-${props.mode}` : 'aurora-dark') as
        'aurora-light' | 'aurora-dark';
    } else if (themeEntry.shaderKind === 'cyber') {
      resolvedShaderVariant = (props.mode ? `cyber-${props.mode}` : 'cyber-dark') as
        'cyber-light' | 'cyber-dark';
    } else if (themeEntry.shaderKind === 'cloth') {
      // Luce cloth (theme charging sims): gold silk on the near-black panel in
      // dark mode, deep gold on warm cream in light mode.
      resolvedShaderVariant = (props.mode ? `cloth-${props.mode}` : 'cloth-dark') as
        'cloth-light' | 'cloth-dark';
    } else if (themeEntry.shaderVariant?.startsWith('fluid-initiative')) {
      // Initiative interactive fluid (charging sims v2): green bursts on
      // near-black in dark mode, green inks on the cream paper in light.
      resolvedShaderVariant = (props.mode ? `fluid-initiative-${props.mode}` : 'fluid-initiative-dark') as
        'fluid-initiative-light' | 'fluid-initiative-dark';
    }
    // Charging sims v2 — the REAL interactive simulations render through
    // SimChargingAnimation (multi-pass ping-pong WebGL): the fluid kind
    // (Initiative bursts / Electric Light marbling) and the physarum
    // agent network (Dark Electric). Everything else stays on the
    // single-pass ShaderChargingAnimation path.
    const isInteractiveSim = themeEntry.shaderKind === 'fluid'
      || themeEntry.shaderKind === 'physarum';
    if (isInteractiveSim) {
      inner = (
        <SimChargingAnimation
          {...resolvedProps}
          simKind={themeEntry.shaderKind as 'fluid' | 'physarum'}
          simVariant={resolvedShaderVariant as FluidVariantId | 'physarum-dark'}
        />
      );
    } else {
      inner = (
        <ShaderChargingAnimation
          {...resolvedProps}
          // Safe narrowing on both fields: the isInteractiveSim guard
          // peeled off every sim variant, and no registry entry carries
          // kind 'physarum' during the sim migration window (the kind
          // returns as a SimChargingAnimation route when Dark Electric's
          // agent sim lands).
          shaderKind={themeEntry.shaderKind as
            Parameters<typeof ShaderChargingAnimation>[0]['shaderKind']}
          shaderVariant={resolvedShaderVariant as
            Parameters<typeof ShaderChargingAnimation>[0]['shaderVariant']}
        />
      );
    }
  } else {
    // Branch 2: skin alias.
    const alias = SKIN_ALIAS_TO_SHADER[themeCode];
    if (alias) {
      inner = (
        <ShaderChargingAnimation
          {...resolvedProps}
          shaderKind={alias.kind}
        />
      );
    } else {
      // Unknown theme: DefaultAnimation fallback.
      inner = <DefaultAnimation {...resolvedProps} />;
    }
  }

  return (
    <div ref={frameRef} className={`${frameClass} ${energySim?styles.energyFrame:''}`} style={style}>
      {visible ? inner : <div className={styles.poster} aria-hidden />}
      {energySim && themeCode.startsWith('skin_') && size>=80 && (
        <div className={`${styles.progress} ${themeCode==='skin_midnight'?styles.progressMoon:styles.progressWarm}`}
          role="progressbar" aria-label="Charging progress" aria-valuemin={0} aria-valuemax={100}
          aria-valuenow={Number.isFinite(props.percent)?Math.max(0,Math.min(100,props.percent)):0}>
          <span style={{width:`${Number.isFinite(props.percent)?Math.max(0,Math.min(100,props.percent)):0}%`}} />
        </div>
      )}
    </div>
  );
}
